import { inject, Injectable } from '@angular/core';
import { SESSION_STORAGE } from '@campus/browser';
import { defer, Observable, of } from 'rxjs';
import { TableStateSnapshotInterface } from './table-state-snapshot.interface';
import { TableStateStore } from './table-state-store.interface';

const STORAGE_PREFIX = 'campus.table-state.';
const INDEX_KEY = `${STORAGE_PREFIX}__index`;

/** Bumped when {@link TableStateSnapshotInterface} changes shape, so older snapshots are discarded rather than misread. */
const SNAPSHOT_VERSION = 1;

/** Upper bound on retained snapshots. Keys can carry a parent id, so the set is not naturally bounded. */
const MAX_ENTRIES = 50;

interface StoredSnapshot {
  version: number;
  state: TableStateSnapshotInterface;
}

/**
 * Keeps the state of opted-in tables in the browser session, keyed by the `persistStateKey` their
 * template supplies.
 *
 * Session-scoped by design: filter state should outlive a navigation but not the tab, so two tabs can
 * hold two different filters on the same list.
 *
 * Every storage access is best-effort: quota errors, disabled storage and hand-edited values all degrade
 * to "no persisted state" rather than breaking the table that asked. Nothing here throws, so the returned
 * observables only ever emit and complete.
 */
@Injectable({ providedIn: 'root' })
export class SessionTableStateStore implements TableStateStore {
  private storage = inject(SESSION_STORAGE);

  public load(key: string): Observable<TableStateSnapshotInterface | null> {
    return defer(() => of(this.readSnapshot(key)));
  }

  public save(key: string, state: TableStateSnapshotInterface): Observable<void> {
    return defer(() => {
      this.writeSnapshot(key, state);

      return of(undefined);
    });
  }

  public clear(key: string): Observable<void> {
    return defer(() => {
      this.removeSnapshot(key);

      return of(undefined);
    });
  }

  private readSnapshot(key: string): TableStateSnapshotInterface | null {
    if (!key) return null;

    const raw = this.read(this.storageKey(key));
    if (!raw) return null;

    let stored: StoredSnapshot;
    try {
      stored = JSON.parse(raw);
    } catch {
      this.removeSnapshot(key);
      return null;
    }

    if (stored?.version !== SNAPSHOT_VERSION || !stored.state) {
      this.removeSnapshot(key);
      return null;
    }

    return stored.state;
  }

  private writeSnapshot(key: string, state: TableStateSnapshotInterface): void {
    if (!key) return;

    const stored: StoredSnapshot = { version: SNAPSHOT_VERSION, state };

    this.write(this.storageKey(key), JSON.stringify(stored));
    this.touchIndex(key);
  }

  private removeSnapshot(key: string): void {
    if (!key) return;

    this.delete(this.storageKey(key));
    this.writeIndex(this.readIndex().filter((entry) => entry !== key));
  }

  private storageKey(key: string): string {
    return `${STORAGE_PREFIX}${key}`;
  }

  /** Moves `key` to the most-recent end of the index and evicts the least recently used overflow. */
  private touchIndex(key: string): void {
    const index = [...this.readIndex().filter((entry) => entry !== key), key];

    while (index.length > MAX_ENTRIES) {
      this.delete(this.storageKey(index.shift()));
    }

    this.writeIndex(index);
  }

  private readIndex(): string[] {
    const raw = this.read(INDEX_KEY);
    if (!raw) return [];

    try {
      const index = JSON.parse(raw);
      return Array.isArray(index) ? index.filter((entry) => typeof entry === 'string') : [];
    } catch {
      return [];
    }
  }

  private writeIndex(index: string[]): void {
    this.write(INDEX_KEY, JSON.stringify(index));
  }

  private read(key: string): string | null {
    try {
      return this.storage.getItem(key);
    } catch {
      return null;
    }
  }

  private write(key: string, value: string): void {
    try {
      this.storage.setItem(key, value);
    } catch {
      // Storage full or unavailable — persisting table state is not worth failing a page render over.
    }
  }

  private delete(key: string): void {
    try {
      this.storage.removeItem(key);
    } catch {
      // See `write`.
    }
  }
}
